import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import {
  Mail,
  Phone,
  MapPin,
  Instagram,
  MessageCircle,
  Send,
} from 'lucide-react';

const Contact: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialService = params.get('service') || '';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: initialService,
    message: '',
  });

  useEffect(() => {
    setFormData((prev) => ({ ...prev, service: initialService }));
    // eslint-disable-next-line
  }, [initialService]);

  useEffect(() => {
    document.title = 'İletişim - Yüksekkare';
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: '',
    });

    setIsSubmitting(false);
    alert(
      'Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.'
    );
  };

  const contactInfo = [
    {
      icon: <Phone size={24} className="text-primary-orange" />,
      title: 'Telefon',
      value: '+90 539 676 81 49',
      link: 'tel:+905396768149',
    },
    {
      icon: <Mail size={24} className="text-primary-orange" />,
      title: 'E-posta',
      value: 'info@yuksekkare.com',
      link: 'mailto:info@yuksekkare.com',
    },
    {
      icon: <MapPin size={24} className="text-primary-orange" />,
      title: 'Adres',
      value: 'Ankara, Türkiye',
      link: null,
    },
  ];

  const socialLinks = [
    {
      icon: <Instagram size={24} />,
      name: 'Instagram',
      url: 'https://instagram.com/yuksekkare',
      color: 'bg-gradient-to-r from-purple-500 to-pink-500',
    },
    {
      icon: <MessageCircle size={24} />,
      name: 'WhatsApp',
      url: 'https://wa.me/+905396768149',
      color: 'bg-green-500',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-navy to-blue-900 text-white px-4 sm:px-6 lg:px-8">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            İletişim
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            Projeniz için bizimle iletişime geçin. Ücretsiz danışmanlık hizmeti
            sunuyoruz.
          </motion.p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="card">
                <h2 className="text-3xl font-bold text-primary-navy mb-6">
                  Mesaj Gönderin
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Ad Soyad *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all"
                        placeholder="Adınız ve soyadınız"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        E-posta *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all"
                        placeholder="ornek@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Telefon
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all"
                        placeholder="+90 5XX XXX XX XX"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="service"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Hizmet Türü
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all"
                      >
                        <option value="">Hizmet seçiniz</option>
                        <option value="urban">Şehir Çekimleri</option>
                        <option value="nature">Doğa & Manzara</option>
                        <option value="events">
                          Etkinlikler & Özel Günler
                        </option>
                        <option value="corporate">Kurumsal Videolar</option>
                        <option value="editing">Fotoğraf Düzenleme</option>
                        <option value="custom">Özel Projeler</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Mesajınız *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all resize-none"
                      placeholder="Projeniz hakkında detayları paylaşın..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Gönderiliyor...</span>
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        <span>Mesaj Gönder</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              {/* Contact Information */}
              <div className="card">
                <h3 className="text-2xl font-bold text-primary-navy mb-6">
                  İletişim Bilgileri
                </h3>

                <div className="space-y-6">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0">{info.icon}</div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {info.title}
                        </h4>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-gray-600 hover:text-primary-orange transition-colors"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-gray-600">{info.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Social Media */}
              <div className="card">
                <h3 className="text-2xl font-bold text-primary-navy mb-6">
                  Sosyal Medya
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`${social.color} text-white p-4 rounded-lg flex items-center justify-center space-x-3 hover:shadow-lg transition-all`}
                    >
                      {social.icon}
                      <span className="font-medium">{social.name}</span>
                    </motion.a>
                  ))}
                </div>
              </div>

              {/* Business Hours */}
              <div className="card">
                <h3 className="text-2xl font-bold text-primary-navy mb-6">
                  Çalışma Saatleri
                </h3>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-medium">Pazartesi - Cuma</span>
                    <span className="text-gray-600">09:00 - 18:00</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-medium">Cumartesi</span>
                    <span className="text-gray-600">10:00 - 16:00</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="font-medium">Pazar</span>
                    <span className="text-gray-600">Kapalı</span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-primary-orange/10 rounded-lg">
                  <p className="text-primary-navy font-medium text-center">
                    Acil durumlar için 7/24 WhatsApp üzerinden ulaşabilirsiniz
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-white px-4 sm:px-6 lg:px-8">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-4">
              Konumumuz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ankara'da hizmet veriyoruz. Projeniz için bölgeye geliyoruz.
            </p>
          </motion.div>

          <div className="bg-gray-200 rounded-2xl overflow-hidden h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d195884.0589256048!2d32.622905!3d39.933363!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14d347d520732db1%3A0xbdc57b0c0842b8d!2sAnkara!5e0!3m2!1str!2str!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Yüksekkare - Ankara"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
